import { z as DocContext, p as jsxRuntimeExports, A as textRendererStyle, B as DynamicMath } from "./doenetml-NmXDuMHr.js";
import React__default, { useContext, useRef } from "react";
import { j as jxg, s as syncLayer, g as syncVisPropValues, e as syncLineStrokeStyle, a as syncWithLabelToggle, f as syncLabelStrokeColor, b as syncLabelMaskCssStyle, i as applyLineFamilyLabelPlacement, k as buildLineFamilyLabelAttributes, d as attachLabelHoverHighlight, c as computeLabelMaskCssStyle, l as stabilizeInitialLineFamilyLabelPlacement, r as removeJXGEventHandlers } from "./JSXGraphRenderer-CoUOmMnA.js";
import { u as useDoenetRenderer } from "./useDoenetRenderer-iiMuPv8N.js";
import { BoardContext, VERTEX_LAYER_OFFSET, LINE_LAYER_OFFSET } from "./graph-CRde30im.js";
import { u as useSourceNavigation, a as attachLineFamilyDragHandlers } from "./lineFamilyDragHandlers-9gNGzIOR.js";
import { ChoiceInputInlineContext } from "./choiceInput-DZ9HPiSL.js";
import { a as buildLineLikeAttributes, c as buildDragHandleAttributes } from "./buildGraphicalAttributes-0RNcbd2S.js";
import { u as usePointerDragState, a as useBoardPointerTracking } from "./useBoardPointerTracking-DV6a8Wvg.js";
import { p as pointerEventToUserCoords } from "./dragThreshold-DuSSFrIU.js";
import { a as resolveLineColor, d as resolveHandleColor } from "./styleColors-Cknqcr0q.js";
import { s as styleToDash } from "./styleToDash-DWb71gbd.js";
import { u as useDraggableRefs, a as useJSXGraphCleanup } from "./useJSXGraphCleanup-CX91oVW8.js";
const vector = React__default.memo(function Vector(props) {
  let { componentIdx, id, SVs, actions, sourceOfUpdate, callAction } = useDoenetRenderer(props);
  Vector.ignoreActionsWithoutCore = () => true;
  const board = useContext(BoardContext);
  const choiceInputInlineContext = useContext(ChoiceInputInlineContext);
  let vectorJXG = useRef(null);
  let point1JXG = useRef(null);
  let point2JXG = useRef(null);
  const dragState = usePointerDragState();
  let headcoords = useRef(null);
  let tailcoords = useRef(null);
  const dragCoordination = {
    draggedTag: useRef(null),
    downOnTag: useRef(null)
  };
  let previousWithLabel = useRef(null);
  let cancelInitialLabelPlacement = useRef(null);
  const {
    lastPositionFromCore: lastPositionsFromCore,
    fixed,
    fixLocation
  } = useDraggableRefs(SVs, SVs.numericalEndpoints);
  let headDraggable = useRef(true);
  let tailDraggable = useRef(true);
  tailDraggable.current = SVs.tailDraggable && !SVs.fixed && !SVs.fixLocation;
  headDraggable.current = SVs.headDraggable && !SVs.fixed && !SVs.fixLocation;
  const { darkMode } = useContext(DocContext) || {};
  const sourceNavigation = useSourceNavigation(id);
  useBoardPointerTracking(board, dragState);
  useJSXGraphCleanup({
    objectRef: vectorJXG,
    destroy: () => deleteVectorJXG(),
    cancelLabelPlacementRef: cancelInitialLabelPlacement
  });
  function createVectorJXG() {
    if (board === null) {
      return null;
    }
    if (SVs.numericalEndpoints.length !== 2 || SVs.numericalEndpoints.some((x) => x.length !== 2)) {
      vectorJXG.current = null;
      point1JXG.current = null;
      point2JXG.current = null;
      return;
    }
    let pointLayer = 10 * SVs.layer + VERTEX_LAYER_OFFSET;
    const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
    var jsxVectorAttributes = {
      ...buildLineLikeAttributes({
        SVs,
        layerOffset: LINE_LAYER_OFFSET,
        fixed: fixed.current,
        fixLocation: fixLocation.current,
        darkMode
      }),
      lastArrow: { type: 1, size: 3, highlightSize: 3 }
    };
    let endpoints = [
      [...SVs.numericalEndpoints[0]],
      [...SVs.numericalEndpoints[1]]
    ];
    let jsxPointAttributes = buildDragHandleAttributes({
      elementAttributes: jsxVectorAttributes,
      layer: pointLayer,
      darkMode,
      showInfoBox: SVs.showCoordsWhenDragging
    });
    let tailPointAttributes = Object.assign({}, jsxPointAttributes);
    let tailVisible = tailDraggable.current && !SVs.hidden;
    tailPointAttributes.visible = tailVisible;
    let newPoint1JXG = board.create(
      "point",
      endpoints[0],
      tailPointAttributes
    );
    let headPointAttributes = Object.assign({}, jsxPointAttributes);
    let headVisible = headDraggable.current && !SVs.hidden;
    headPointAttributes.visible = headVisible;
    let newPoint2JXG = board.create(
      "point",
      endpoints[1],
      headPointAttributes
    );
    jsxVectorAttributes.label = buildLineFamilyLabelAttributes({
      labelForGraph: SVs.labelForGraph,
      labelPosition: SVs.labelPosition,
      labelHasLatex: SVs.labelHasLatex,
      applyStyleToLabel: SVs.applyStyleToLabel,
      lineColor,
      layer: SVs.layer,
      maskLabel: SVs.maskLabel
    });
    let newVectorJXG = board.create(
      "arrow",
      [newPoint1JXG, newPoint2JXG],
      jsxVectorAttributes
    );
    newVectorJXG.isDraggable = !fixLocation.current;
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    function applyDragReset() {
      vectorJXG.current?.point1.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        lastPositionsFromCore.current[0]
      );
      vectorJXG.current?.point2.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        lastPositionsFromCore.current[1]
      );
    }
    attachLineFamilyDragHandlers({
      sourceNavigation,
      jxg: newVectorJXG,
      tag: 0,
      dragState,
      coordination: dragCoordination,
      componentIdx,
      callAction,
      fixedRef: fixed,
      participatesInDownTag: false,
      suppressClickWhenDownOnOtherTag: true,
      // vector body's `down` always dispatches focus, regardless of
      // whether a child got down first (matches existing behavior).
      actions: {
        move: actions.moveVector,
        focus: actions.vectorFocused,
        click: actions.vectorClicked
      },
      snapshot: () => [
        [...newVectorJXG.point1.coords.scrCoords],
        [...newVectorJXG.point2.coords.scrCoords]
      ],
      buildTransientMoveArgs: (e, snap) => {
        const viaPointer = e.type === "pointermove";
        if (viaPointer && dragState.pointerAtDown.current && snap) {
          tailcoords.current = pointerEventToUserCoords(
            e,
            dragState.pointerAtDown.current,
            snap[0],
            board
          );
          headcoords.current = pointerEventToUserCoords(
            e,
            dragState.pointerAtDown.current,
            snap[1],
            board
          );
        } else {
          tailcoords.current = [
            newVectorJXG.point1.X(),
            newVectorJXG.point1.Y()
          ];
          headcoords.current = [
            newVectorJXG.point2.X(),
            newVectorJXG.point2.Y()
          ];
        }
        return {
          headcoords: headcoords.current,
          tailcoords: tailcoords.current,
          transient: true,
          skippable: true
        };
      },
      buildCommitMoveArgs: () => ({
        headcoords: headcoords.current,
        tailcoords: tailcoords.current
      }),
      onDragApplied: () => {
        if (dragCoordination.draggedTag.current !== 0) {
          return;
        }
        applyDragReset();
      }
    });
    function attachVectorEndpointHandlers(point, tagN, argKey, coordsRef, isDraggable) {
      attachLineFamilyDragHandlers({
        sourceNavigation,
        jxg: point,
        tag: tagN,
        dragState,
        coordination: dragCoordination,
        componentIdx,
        callAction,
        fixedRef: fixed,
        shouldDispatchFocusOnDown: () => isDraggable.current,
        actions: {
          move: actions.moveVector,
          focus: actions.vectorFocused,
          click: actions.vectorClicked
        },
        snapshot: () => null,
        buildTransientMoveArgs: () => {
          coordsRef.current = [point.X(), point.Y()];
          return {
            [argKey]: coordsRef.current,
            transient: true,
            skippable: true,
            sourceDetails: { vertex: tagN - 1 }
          };
        },
        buildCommitMoveArgs: () => ({
          [argKey]: coordsRef.current
        }),
        onDragApplied: () => {
          if (dragCoordination.draggedTag.current !== tagN) {
            return;
          }
          applyDragReset();
          if (board) {
            board.updateInfobox(point);
          }
        }
      });
    }
    attachVectorEndpointHandlers(
      newPoint1JXG,
      1,
      "tailcoords",
      tailcoords,
      tailDraggable
    );
    attachVectorEndpointHandlers(
      newPoint2JXG,
      2,
      "headcoords",
      headcoords,
      headDraggable
    );
    vectorJXG.current = newVectorJXG;
    point1JXG.current = newPoint1JXG;
    point2JXG.current = newPoint2JXG;
    attachLabelHoverHighlight({
      hoverTargetJXG: newVectorJXG,
      getLabelJXG: () => vectorJXG.current?.label,
      ...computeLabelMaskCssStyle({
        layer: SVs.layer,
        masked: SVs.maskLabel
      }),
      board
    });
    if (SVs.labelForGraph !== "" && newVectorJXG.hasLabel) {
      cancelInitialLabelPlacement.current = stabilizeInitialLineFamilyLabelPlacement({
        board,
        lineLike: newVectorJXG,
        applyPlacement: (forceFullUpdate) => {
          if (vectorJXG.current !== newVectorJXG || !newVectorJXG.hasLabel) {
            return false;
          }
          applyLineFamilyLabelPlacement({
            board,
            lineLike: newVectorJXG,
            labelPosition: SVs.labelPosition,
            forceFullUpdate,
            setNeedsUpdateOnNoChange: true
          });
          return true;
        }
      });
    }
    previousWithLabel.current = SVs.labelForGraph !== "";
  }
  function deleteVectorJXG() {
    cancelInitialLabelPlacement.current?.();
    cancelInitialLabelPlacement.current = null;
    if (vectorJXG.current) {
      removeJXGEventHandlers(vectorJXG.current);
      board?.removeObject(vectorJXG.current);
      vectorJXG.current = null;
    }
    if (point1JXG.current) {
      removeJXGEventHandlers(point1JXG.current);
      board?.removeObject(point1JXG.current);
      point1JXG.current = null;
    }
    if (point2JXG.current) {
      removeJXGEventHandlers(point2JXG.current);
      board?.removeObject(point2JXG.current);
      point2JXG.current = null;
    }
  }
  if (board) {
    if (vectorJXG.current === null || point1JXG.current === null || point2JXG.current === null) {
      createVectorJXG();
    } else if (SVs.numericalEndpoints.length !== 2 || SVs.numericalEndpoints.some((x) => x.length !== 2)) {
      deleteVectorJXG();
    } else {
      let validPoints = true;
      for (let coords of [
        SVs.numericalEndpoints[0],
        SVs.numericalEndpoints[1]
      ]) {
        if (!Number.isFinite(coords[0])) {
          validPoints = false;
        }
        if (!Number.isFinite(coords[1])) {
          validPoints = false;
        }
      }
      vectorJXG.current.point1.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalEndpoints[0]
      );
      vectorJXG.current.point2.coords.setCoordinates(
        jxg.COORDS_BY_USER,
        SVs.numericalEndpoints[1]
      );
      let visible = !SVs.hidden && validPoints;
      let tailVisible = tailDraggable.current && visible;
      let headVisible = headDraggable.current && visible;
      vectorJXG.current.visProp.fixed = fixed.current;
      vectorJXG.current.visProp.highlight = !fixLocation.current;
      vectorJXG.current.isDraggable = !fixLocation.current;
      vectorJXG.current.visProp["visible"] = visible;
      vectorJXG.current.visPropCalc["visible"] = visible;
      point1JXG.current.visProp["visible"] = tailVisible;
      point1JXG.current.visPropCalc["visible"] = tailVisible;
      point2JXG.current.visProp["visible"] = headVisible;
      point2JXG.current.visPropCalc["visible"] = headVisible;
      point1JXG.current.visProp.showinfobox = SVs.showCoordsWhenDragging;
      point2JXG.current.visProp.showinfobox = SVs.showCoordsWhenDragging;
      if (sourceOfUpdate.sourceInformation && componentIdx in sourceOfUpdate.sourceInformation) {
        let sourceInfo = sourceOfUpdate.sourceInformation[componentIdx];
        if (typeof sourceInfo === "object" && sourceInfo && "vertex" in sourceInfo) {
          if (sourceInfo.vertex === 0) {
            board.updateInfobox(point1JXG.current);
          } else if (sourceInfo.vertex === 1) {
            board.updateInfobox(point2JXG.current);
          }
        }
      }
      if (syncLayer(vectorJXG.current, SVs.layer, LINE_LAYER_OFFSET)) {
        const pointLayer = 10 * SVs.layer + VERTEX_LAYER_OFFSET;
        point1JXG.current.setAttribute({ layer: pointLayer });
        point2JXG.current.setAttribute({ layer: pointLayer });
      }
      const lineColor = resolveLineColor(SVs.selectedStyle, darkMode);
      const handleColor = resolveHandleColor(darkMode);
      syncVisPropValues(point1JXG.current, {
        highlightfillcolor: handleColor
      });
      syncVisPropValues(point2JXG.current, {
        highlightfillcolor: handleColor
      });
      syncLineStrokeStyle(vectorJXG.current, {
        lineColor,
        lineWidth: SVs.selectedStyle.lineWidth,
        lineOpacity: SVs.selectedStyle.lineOpacity,
        dash: styleToDash(SVs.selectedStyle.lineStyle)
      });
      vectorJXG.current.name = SVs.labelForGraph;
      syncWithLabelToggle(
        vectorJXG.current,
        SVs.labelForGraph,
        previousWithLabel
      );
      vectorJXG.current.needsUpdate = true;
      vectorJXG.current.update();
      if (vectorJXG.current.hasLabel && vectorJXG.current.label) {
        syncLabelStrokeColor(
          vectorJXG.current.label,
          SVs.applyStyleToLabel,
          lineColor
        );
        syncLabelMaskCssStyle(vectorJXG.current.label, SVs.layer, {
          maskLabel: SVs.maskLabel
        });
        applyLineFamilyLabelPlacement({
          board,
          lineLike: vectorJXG.current,
          labelPosition: SVs.labelPosition,
          setNeedsUpdateOnNoChange: true
        });
      }
      point1JXG.current.needsUpdate = true;
      point1JXG.current.update();
      point2JXG.current.needsUpdate = true;
      point2JXG.current.update();
      board.updateRenderer();
    }
    return /* @__PURE__ */ jsxRuntimeExports.jsx(jsxRuntimeExports.Fragment, { children: /* @__PURE__ */ jsxRuntimeExports.jsx("span", { id }) });
  }
  if (SVs.hidden) {
    return null;
  }
  const mathJaxify = "\\(" + SVs.latex + "\\)";
  const style = !choiceInputInlineContext.inOption ? textRendererStyle(darkMode ?? "light", SVs.selectedStyle) : void 0;
  return /* @__PURE__ */ jsxRuntimeExports.jsx("span", { style, id, children: /* @__PURE__ */ jsxRuntimeExports.jsx(DynamicMath, { latex: mathJaxify }) });
});
export {
  vector as default
};
